// StatusCell — the one Status column every list of customers or instances draws.
//
// A row is one of three things, and the cell says which from the two facts the
// service carries on it and nothing else: `retired` (the relationship ended —
// when, why, who) and `superseded_by` (the customer that replaced this one).
// Nothing is inferred from subscriptions, counts or names: a customer with no
// subscriptions is a customer who has not deployed yet, not a former one, and
// a cell that guessed would hide a live relationship behind a word.
//
//   Retired <date>            — the RetiredBadge, reason and who on hover
//   Superseded → <successor>  — a link that opens the successor
//   Active                    — muted text, no badge: the ordinary case should
//                               not shout
//
// A row that is both retired and superseded reads Retired — the later,
// terminal fact — and keeps the successor link beneath it, because the reader
// following the chain still needs somewhere to go.
//
// An instance list passes `supersededBy={null}`: an instance cannot be
// superseded, so its cell is only ever Active or Retired.

import { Button } from '@miq/design-system/components/ui/button';

import type { SupersededBy } from '../data/acrossCustomers';
import type { Retired } from '../data/reads';
import { RetiredBadge } from './RetiredBadge';

export function StatusCell({
  retired,
  supersededBy,
  onOpenCustomer,
}: {
  retired: Retired | null;
  supersededBy: SupersededBy | null;
  /**
   * Opens the successor, where there is one. Which screen that lands on is the
   * page's business — All Customers opens the drill-in, Fleet Revenue the
   * successor's Billing page — so the page supplies it.
   */
  onOpenCustomer?: (publicId: string) => void;
}) {
  if (retired === null && supersededBy === null) {
    return <span className="text-muted-foreground">Active</span>;
  }
  return (
    <div className="space-y-1">
      {retired !== null && (
        <div>
          <RetiredBadge retired={retired} />
        </div>
      )}
      {supersededBy !== null && <SupersededBy successor={supersededBy} onOpenCustomer={onOpenCustomer} />}
    </div>
  );
}

/** "Superseded → <name>", the name a link to the successor where the page can open one. */
function SupersededBy({
  successor,
  onOpenCustomer,
}: {
  successor: SupersededBy;
  onOpenCustomer?: (publicId: string) => void;
}) {
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="text-muted-foreground">Superseded →</span>
      {onOpenCustomer ? (
        <Button
          variant="link"
          className="h-auto p-0 text-sm"
          onPress={() => onOpenCustomer(successor.public_id)}
          aria-label={`Open ${successor.display_name}, which replaced this customer`}
        >
          {successor.display_name}
        </Button>
      ) : (
        <span>{successor.display_name}</span>
      )}
    </div>
  );
}
